import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Lock, Save, X, Calendar, TrendingUp, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import Skeleton from './Skeleton';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

interface HiddenRevenueEntry {
  id: string;
  date: string;
  amount: number;
  local_id?: string; // For optimistic UI updates
}

interface HiddenRevenueFormData {
  date: string;
  amount: string;
}

const ITEMS_PER_PAGE = 10;

export default function HiddenRevenue() {
  const [entries, setEntries] = useState<HiddenRevenueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<HiddenRevenueEntry | null>(null);
  const [formData, setFormData] = useState<HiddenRevenueFormData>({
    date: format(new Date(), 'yyyy-MM-dd'),
    amount: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [weeklyTotal, setWeeklyTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchEntries();
    fetchTotals();
  }, [currentPage, deletedIds]);

  const fetchTotals = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('hidden_revenue')
        .select('*');

      if (error) throw error;
      
      const validEntries = data?.filter(entry => !deletedIds.has(entry.id)) || [];
      
      // Calculate grand total
      const total = validEntries.reduce((sum, entry) => sum + entry.amount, 0);
      setGrandTotal(total);
      
      // Calculate monthly total
      const currentMonth = format(new Date(), 'yyyy-MM');
      const monthlyTotal = validEntries
        .filter(entry => format(parseISO(entry.date), 'yyyy-MM') === currentMonth)
        .reduce((sum, entry) => sum + entry.amount, 0);
      setMonthlyTotal(monthlyTotal);
      
      // Calculate weekly total with 7 AM cutoff
      const now = new Date();
      const adjustedDateForWeek = now.getHours() < 7
        ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
        : now;
      const weekStart = startOfWeek(adjustedDateForWeek, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(adjustedDateForWeek, { weekStartsOn: 1 });
      const weeklyTotal = validEntries
        .filter(entry => {
          const date = parseISO(entry.date);
          return date >= weekStart && date <= weekEnd;
        })
        .reduce((sum, entry) => sum + entry.amount, 0);
      setWeeklyTotal(weeklyTotal);
      
    } catch (error) {
      console.error('Error fetching totals:', error);
    }
  };

  const fetchEntries = async () => {
    try {
      // First get total count for pagination
      const { count, error: countError } = await supabase
        .from('hidden_revenue')
        .select('id', { count: 'exact' });
      
      if (countError) throw countError;
      setTotalCount(count || 0);

      // Then get paginated data
      let query = supabase
        .from('hidden_revenue')
        .select('*')
        .order('date', { ascending: false })
        .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

      const { data, error } = await query;

      if (error) throw error;
      
      // Filter out any entries that have been marked as deleted
      const filteredData = data?.filter(entry => !deletedIds.has(entry.id)) || [];
      
      // Add local_id for tracking
      const entriesWithLocalId = filteredData.map(entry => ({
        ...entry,
        local_id: uuidv4()
      }));
      
      setEntries(entriesWithLocalId);
    } catch (error) {
      console.error('Error fetching hidden revenue:', error);
      toast.error('Fout bij het ophalen van gegevens');
    } finally {
      setLoading(false);
    }
  };

  const handleAddShiftRevenue = () => {
    // Get current date/time
    const now = new Date();
    // If it's before 7 AM, the revenue belongs to the previous day
    const shiftDate = now.getHours() < 7
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
      : now;
    
    setSelectedDate(format(shiftDate, 'yyyy-MM-dd'));
    setShowRevenueForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Determine the correct date based on the time
      const submitDate = new Date();
      const actualDate = submitDate.getHours() < 7
        ? new Date(submitDate.getFullYear(), submitDate.getMonth(), submitDate.getDate() - 1)
        : submitDate;

      const amount = parseFloat(formData.amount.replace(',', '.'));

      if (editingEntry) {
        // Optimistic update
        const updatedEntries = entries.map(entry => 
          entry.id === editingEntry.id 
            ? { ...entry, date: formData.date, amount } 
            : entry
        );
        setEntries(updatedEntries);
        
        const { error } = await supabase
          .from('hidden_revenue')
          .update({ 
            date: formData.date, 
            amount: amount
          })
          .eq('id', editingEntry.id);

        if (error) {
          // Revert optimistic update on error
          setEntries(entries);
          throw error;
        }
        
        toast.success('Omzet bijgewerkt');
      } else {
        // Create a temporary local ID for optimistic UI
        const tempLocalId = uuidv4();
        
        // Add to UI immediately (optimistic update)
        const newEntry = {
          id: tempLocalId,
          local_id: tempLocalId,
          date: formData.date,
          amount: amount,
          user_id: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setEntries(prev => [newEntry, ...prev]);
        
        // Then save to database
        const { data, error } = await supabase
          .from('hidden_revenue')
          .insert([{
            user_id: user.id,
            date: formData.date,
            amount: amount
          }])
          .select()
          .maybeSingle();

        if (error) {
          // Remove optimistic entry on error
          setEntries(prev => prev.filter(entry => entry.local_id !== tempLocalId));
          throw error;
        }
        
        // Replace temporary entry with real one from database
        if (data) {
          setEntries(prev => 
            prev.map(entry => 
              entry.local_id === tempLocalId ? { ...data, local_id: tempLocalId } : entry
            )
          );
        }
        
        toast.success('Omzet toegevoegd');
      }

      setFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        amount: '',
      });
      setShowForm(false);
      setEditingEntry(null);
      
      // Refresh to ensure data is in sync
      fetchEntries();
      fetchTotals();
    } catch (error) {
      console.error('Error saving hidden revenue:', error);
      toast.error('Fout bij het opslaan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Weet je zeker dat je deze omzet wilt verwijderen?')) return;

    try {
      // Mark as deleting
      setDeletingIds(prev => new Set([...prev, id]));

      // Remove from UI immediately
      setEntries(prev => prev.filter(entry => entry.id !== id));
      setDeletedIds(prev => new Set([...prev, id]));
      setTotalCount(prev => Math.max(0, prev - 1));

      // Delete from database
      const { error } = await supabase
        .from('hidden_revenue')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      toast.success('Omzet verwijderd');
      
      // Update pagination if needed
      if (entries.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      }
      
      // Refresh totals
      fetchTotals();
    } catch (error) {
      console.error('Error deleting hidden revenue:', error);
      toast.error('Fout bij het verwijderen');
      
      // Revert UI changes on error
      await fetchEntries();
      await fetchTotals();
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  // Filter out any entries that have been marked as deleted
  const displayedEntries = entries;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Stats Card Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="space-y-4">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-8 w-40" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ))}
        </div>

        {/* Entries List Skeleton */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {[...Array(3)].map((_, i) => (
                    <th key={i} className="px-6 py-3">
                      <Skeleton className="h-4 w-24" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {[...Array(3)].map((_, colIndex) => (
                      <td key={colIndex} className="px-6 py-4">
                        <Skeleton className="h-6 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  const currentMonthEntries = displayedEntries.filter(entry => 
    format(parseISO(entry.date), 'yyyy-MM') === format(new Date(), 'yyyy-MM')
  );

  // Define week start and end for display with 7 AM cutoff
  const now = new Date();
  const adjustedDateForWeek = now.getHours() < 7
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
    : now;
  const weekStart = startOfWeek(adjustedDateForWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(adjustedDateForWeek, { weekStartsOn: 1 });

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Fooi
        </h1>
        <button
          onClick={() => {
            setEditingEntry(null);
            setFormData({
              date: format(new Date(), 'yyyy-MM-dd'),
              amount: '',
            });
            setShowForm(true);
          }}
          className="w-full lg:w-auto flex items-center justify-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <Calendar className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400" />
          Nieuwe Invoer
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Deze Week
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{weeklyTotal.toFixed(2)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {format(weekStart, 'dd/MM')} - {format(weekEnd, 'dd/MM')}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Deze Maand
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{monthlyTotal.toFixed(2)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {format(new Date(), 'MMMM yyyy', { locale: nl })}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Totaal
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{grandTotal.toFixed(2)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Alle omzet
          </p>
        </div>
      </div>

      {/* Entries Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Datum
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Bedrag
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {displayedEntries.map((entry) => (
                <tr key={entry.local_id || entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {format(parseISO(entry.date), 'dd-MM-yyyy')}
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                      {format(parseISO(entry.date), 'EEEE', { locale: nl })}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-gray-100">
                    €{entry.amount.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        setEditingEntry(entry);
                        setFormData({
                          date: entry.date,
                          amount: entry.amount.toString().replace('.', ','),
                        });
                        setShowForm(true);
                      }}
                      className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 mr-2"
                      disabled={deletingIds.has(entry.id)}
                    >
                      <Pencil className="h-5 w-5 inline-block" />
                    </button>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                      disabled={deletingIds.has(entry.id)}
                    >
                      {deletingIds.has(entry.id) ? (
                        <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                      ) : (
                        <Trash2 className="h-5 w-5 inline-block" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
              {displayedEntries.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    Geen omzet gevonden
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Pagina <span className="font-medium">{currentPage}</span> van{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[99999]">
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => {
              setShowForm(false);
              setEditingEntry(null);
            }}
          />
          
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {editingEntry ? 'Omzet Bewerken' : 'Nieuwe Omzet'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditingEntry(null);
                    }}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Datum
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Bedrag (€) *
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="text"
                        inputMode="decimal"
                        required
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="0,00"
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setEditingEntry(null);
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Annuleren
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !formData.amount}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {editingEntry ? 'Opslaan...' : 'Toevoegen...'}
                        </div>
                      ) : (
                        <>
                          <Save className="h-4 w-4 inline-block mr-2" />
                          {editingEntry ? 'Opslaan' : 'Toevoegen'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}